import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { comics } from '@/mocks/comics';
import type { ComicChapter } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find(c => c.id === Number(id));
  const [readingChapter, setReadingChapter] = useState<ComicChapter | null>(null);

  if (!comic) {
    return (
      <>
        <SEOHead
          title="漫画未找到"
          description="抱歉，您要查找的漫画不存在。欢迎浏览爱漫画其他精彩韩国漫画作品。"
          canonicalUrl="https://aimanhua.com/"
        />
        <ScrollToTop />
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '漫画未找到' }]} />
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-20 h-20 flex items-center justify-center text-foreground-300 mb-4">
                <i className="ri-file-unknow-line text-5xl"></i>
              </div>
              <h1 className="text-xl font-semibold text-foreground-900 mb-2">漫画未找到</h1>
              <p className="text-sm text-foreground-500 mb-6">您要查找的漫画不存在或已被移除</p>
              <Link to="/" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 whitespace-nowrap cursor-pointer transition-colors duration-200">
                返回首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '分类浏览', href: '/category' },
    { label: comic.title },
  ];

  const relatedComics = comics
    .filter(c => c.id !== comic.id)
    .filter(c => c.categories.some(cat => comic.categories.includes(cat)))
    .slice(0, 6);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 无删减韩国漫画`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: `${comic.publishYear}-01-01`,
    dateModified: comic.updateDate,
    publisher: {
      '@type': 'Organization',
      name: '爱漫画',
      url: 'https://aimanhua.com/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://aimanhua.com/article/${comic.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://aimanhua.com/' },
      { '@type': 'ListItem', position: 2, name: '漫画分类', item: 'https://aimanhua.com/category' },
      { '@type': 'ListItem', position: 3, name: comic.title },
    ],
  };

  return (
    <>
      <SEOHead
        title={comic.title}
        description={`${comic.title} - ${comic.shortDescription}爱漫画提供无删减韩国漫画下拉式在线阅读，作者${comic.author}，评分${comic.rating}分。`}
        keywords={`${comic.title},韩国漫画,无删减漫画,${comic.tags.slice(0, 3).join(',')},爱漫画`}
        canonicalUrl={`https://aimanhua.com/article/${comic.id}`}
        ogType="article"
        schemaJson={articleSchema}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <ScrollToTop />
      <Navbar />
      <main>
        <div className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <div className="flex flex-col lg:flex-row gap-8 md:gap-10 pb-10">
              <div className="lg:w-[300px] shrink-0">
                <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-100 sticky top-24">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - 韩国漫画`}
                    title={`${comic.title} - 无删减韩国漫画 下拉式阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-sm text-foreground-600">作者：<strong>{comic.author}</strong></span>
                  <span className="text-foreground-300">|</span>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                    comic.status === '连载中'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-foreground-200 text-foreground-600'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="text-foreground-300">|</span>
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500 text-sm"></i>
                    <span className="text-sm font-semibold text-foreground-900">{comic.rating}</span>
                    <span className="text-xs text-foreground-500">分</span>
                  </div>
                  <span className="text-foreground-300">|</span>
                  <span className="text-xs text-foreground-500">
                    <i className="ri-eye-line mr-1"></i>
                    {(comic.views / 10000).toFixed(0)}万次阅读
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-5">
                  {comic.categories.map((catId) => {
                    const catObj = { romance: '恋爱', fantasy: '奇幻', action: '动作', suspense: '悬疑', comedy: '搞笑', drama: '剧情', horror: '惊悚', school: '校园', workplace: '职场' }[catId] || catId;
                    return (
                      <Link
                        key={catId}
                        to={`/category?cat=${catId}`}
                        className="px-2.5 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium hover:bg-secondary-200 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                      >
                        {catObj}
                      </Link>
                    );
                  })}
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 bg-background-100 text-foreground-500 rounded-full text-xs whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-xl p-4 md:p-5 mb-6">
                  <h2 className="text-base font-semibold text-foreground-900 mb-3">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                  <p className="text-xs text-foreground-400 mt-3">
                    更新时间：{comic.updateDate} · 首发年份：{comic.publishYear}年
                  </p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-base font-semibold text-foreground-900">
                      章节目录 <span className="text-sm text-foreground-500 font-normal">（共{comic.chapters.length}话）</span>
                    </h2>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-[400px] overflow-y-auto pr-1">
                    {comic.chapters.map((chapter) => (
                      <button
                        key={chapter.id}
                        onClick={() => setReadingChapter(chapter)}
                        className={`text-left px-3 py-2 rounded-lg text-sm transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                          readingChapter?.id === chapter.id
                            ? 'bg-primary-500 text-background-50'
                            : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                        }`}
                      >
                        <span className="block truncate">{chapter.title}</span>
                        <span className={`text-xs mt-0.5 block ${
                          readingChapter?.id === chapter.id ? 'text-background-50/70' : 'text-foreground-400'
                        }`}>
                          {chapter.updateDate}
                        </span>
                      </button>
                    ))}
                  </div>

                  {readingChapter && (
                    <div className="mt-4 p-4 bg-accent-50 border border-accent-200 rounded-xl">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-foreground-900">
                            正在阅读：{readingChapter.title}
                          </p>
                          <p className="text-xs text-foreground-500 mt-0.5">
                            爱漫画提供无删减韩国漫画下拉式在线阅读体验
                          </p>
                        </div>
                        <span className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer">
                          <i className="ri-play-line mr-1.5"></i>开始阅读
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {relatedComics.length > 0 && (
              <div className="border-t border-background-200 py-14">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                  {relatedComics.map((c) => (
                    <ComicCard key={c.id} comic={c} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}